import type { SpotListItem } from "@divespots/contracts";
import { isClosedOn } from "@divespots/contracts";
import type { Marker, MarkerClusterGroup } from "leaflet";
import "leaflet.markercluster/dist/MarkerCluster.css";
import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import { spotPinHtml } from "../components/Pin.tsx";
import { L } from "./leaflet-global.ts";
import "leaflet.markercluster";

interface Props {
  spots: readonly SpotListItem[];
  selectedSlug: string | undefined;
  onSelect: (slug: string) => void;
  /** Accessible names; `{{name}}` / `{{count}}` are filled in by the caller's t(). */
  pinLabel: (spot: SpotListItem, closed: boolean) => string;
  clusterLabel: (count: number) => string;
  now?: Date;
}

const PIN_BOX = 44; // Leaflet icon box = the 44px hit target; the 32px pin is centred inside.

function pinIcon(selected: boolean, closed: boolean, permanent: boolean) {
  return L.divIcon({
    className: "spot-marker",
    html: spotPinHtml({ selected, closed, permanent }),
    iconSize: [PIN_BOX, PIN_BOX],
    iconAnchor: [PIN_BOX / 2, PIN_BOX / 2],
  });
}

/** Spot pins in a markercluster group; cluster bubbles are the design's `ocean` circle with a count. */
export function SpotCluster({ spots, selectedSlug, onSelect, pinLabel, clusterLabel, now }: Props) {
  const map = useMap();
  const groupRef = useRef<MarkerClusterGroup | null>(null);
  const markers = useRef(
    new Map<string, { marker: Marker; closed: boolean; permanent: boolean }>(),
  );
  const selectRef = useRef(onSelect);
  const labelRef = useRef({ pinLabel, clusterLabel });
  const selectedRef = useRef(selectedSlug);
  selectRef.current = onSelect;
  labelRef.current = { pinLabel, clusterLabel };
  selectedRef.current = selectedSlug;

  // (Re)build the markers when the spot list or the language (labels) changes.
  useEffect(() => {
    const group = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 56,
      spiderfyOnMaxZoom: true,
      zoomToBoundsOnClick: true,
      iconCreateFunction: (cluster) => {
        const count = cluster.getChildCount();
        const size = count >= 100 ? "lg" : count >= 10 ? "md" : "sm";
        const label = labelRef.current.clusterLabel(count);
        return L.divIcon({
          className: "cluster-marker",
          html: `<div class="cluster-bubble cluster-bubble--${size}"><span aria-hidden="true">${count}</span><span class="sr-only">${label}</span></div>`,
          iconSize: [44, 44],
          iconAnchor: [22, 22],
        });
      },
    });
    const byslug = markers.current;
    byslug.clear();
    const when = now ?? new Date();
    for (const spot of spots) {
      const closed = isClosedOn(spot.closedPeriods, when) !== null;
      const permanent = spot.permanentClosure !== null;
      const name = labelRef.current.pinLabel(spot, closed);
      const marker = L.marker([spot.location.lat, spot.location.lon], {
        icon: pinIcon(spot.slug === selectedRef.current, closed, permanent),
        title: name,
        alt: name,
        keyboard: true,
        riseOnHover: true,
      });
      marker.on("add", () => marker.getElement()?.setAttribute("aria-label", name));
      marker.on("click", () => selectRef.current(spot.slug));
      byslug.set(spot.slug, { marker, closed, permanent });
      group.addLayer(marker);
    }
    map.addLayer(group);
    groupRef.current = group;
    return () => {
      map.removeLayer(group);
      groupRef.current = null;
      byslug.clear();
    };
  }, [map, spots, pinLabel, clusterLabel, now]);

  // Selected pin grows and gets its ring.
  const prev = useRef<string | undefined>(undefined);
  useEffect(() => {
    const entries = markers.current;
    const old = prev.current ? entries.get(prev.current) : undefined;
    old?.marker.setIcon(pinIcon(false, old.closed, old.permanent));
    const cur = selectedSlug ? entries.get(selectedSlug) : undefined;
    cur?.marker.setIcon(pinIcon(true, cur.closed, cur.permanent));
    prev.current = selectedSlug;
  }, [selectedSlug, spots, pinLabel, clusterLabel, now]);

  return null;
}
